import { Box, MenuItem, Select, Switch, Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import useAppTheme from 'hooks/useAppTheme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { ThemePalette } from 'mui.theme';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useShiftConfig from 'hooks/useShiftConfig';
import { formatShiftHour } from 'utils/helpers/customerShiftHelpers';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  // Add your component props here
  shiftWiseAnalysis: boolean;
  setShiftWiseAnalysis: any;
  shiftNumber: number;
  setShiftNumber: any;
  disabled?: boolean;
}
const today = dayjs().utc().set('hour', 0).set('minute', 0).set('second', 0);

const ShiftwiseToggler = ({
  setShiftNumber,
  setShiftWiseAnalysis,
  shiftNumber,
  shiftWiseAnalysis,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { shiftOptions } = useShiftConfig();

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Typography
        style={{ fontSize: '11px', color: enableDarkTheme ? 'white' : 'black' }}
      >
        {t('shift_wise_analysis')}
      </Typography>
      <Switch
        checked={shiftWiseAnalysis}
        disabled={disabled}
        onChange={(e) => {
          setShiftWiseAnalysis(e.target.checked);
        }}
        color="secondary"
      />
      {shiftWiseAnalysis && (
        <Select
          labelId="shift-number-label"
          value={shiftNumber}
          defaultValue={shiftNumber}
          disabled={disabled}
          onChange={(e) => {
            setShiftNumber(e.target.value);
          }}
          sx={{
            height: '30px',
            fontSize: '11px',
            marginLeft: '10px',
            marginRight: '20px',
            backgroundColor: enableDarkTheme
              ? ThemePalette.dark.cartesianGrid
              : palette.boxBackground,
            color: palette.textPrimary,
            '& .MuiSelect-icon': {
              color: palette.textSecondary,
            },
            '& fieldset': {
              borderColor: palette.textPrimary,
            },
            ...(enableDarkTheme && {
              '&.MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: palette.cardBorderColor, // <------------------ outline-color by default
                },
              },
            }),
          }}
          MenuProps={{
            PaperProps: {
              style: {
                backgroundColor: enableDarkTheme
                  ? palette.menuOptions
                  : palette.boxBackground,
                color: palette.textPrimary,
                fontSize: '11px',
              },
            },
          }}
        >
          {shiftOptions.map((shift, index) => {
            const todayLocal = dayjs().tz(timezone);
            const startTimeStr = formatShiftHour(shift.startHour, todayLocal);
            const endTimeStr = formatShiftHour(shift.endHour, todayLocal);
            const label = shift.shiftName ?? `Shift ${index + 1}`;

            return (
              <MenuItem
                key={index}
                value={index + 1}
                sx={{
                  fontSize: '11px',
                }}
              >
                {`${label} ${startTimeStr} - ${endTimeStr}`}
              </MenuItem>
            );
          })}
        </Select>
      )}
    </Box>
  );
};

export default ShiftwiseToggler;
